import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import GenerationProgressIndicator from '../../components/ui/GenerationProgressIndicator';
import GenerationStepCard from './components/GenerationStepCard';
import LiveActivityFeed from './components/LiveActivityFeed';
import PromptSummaryCard from './components/PromptSummaryCard';
import CancelGenerationModal from './components/CancelGenerationModal';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const CodeGeneration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [estimatedTime, setEstimatedTime] = useState(45);
  const [activities, setActivities] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const promptData = location?.state || {
    prompt: "Build a modern e-commerce platform with product catalog, shopping cart, and secure checkout",
    projectType: "Full-Stack Web Application",
    techStack: ["React", "Node.js", "PostgreSQL", "Stripe"]
  };

  const generationSteps = [
    {
      id: 1,
      label: 'Analyzing Requirements',
      icon: 'Brain',
      description: 'Processing your prompt and identifying project structure'
    },
    {
      id: 2,
      label: 'Generating Frontend',
      icon: 'Layout',
      description: 'Creating React components, pages, and routing configuration'
    },
    {
      id: 3,
      label: 'Creating Backend',
      icon: 'Server',
      description: 'Building API routes, controllers, and middleware'
    },
    {
      id: 4,
      label: 'Building Database Schema',
      icon: 'Database',
      description: 'Generating SQL schemas, migrations, and seed data'
    },
    {
      id: 5,
      label: 'Packaging Project',
      icon: 'Package',
      description: 'Organizing files and preparing downloadable ZIP'
    }
  ];

  const mockActivities = [
    { fileName: 'package.json', path: '/root', timestamp: 'Just now' },
    { fileName: 'App.jsx', path: '/src', timestamp: '2s ago' },
    { fileName: 'index.css', path: '/src/styles', timestamp: '4s ago' },
    { fileName: 'ProductCard.jsx', path: '/src/components', timestamp: '6s ago' },
    { fileName: 'api.routes.js', path: '/backend/routes', timestamp: '8s ago' },
    { fileName: 'products.schema.sql', path: '/database/schemas', timestamp: '10s ago' },
    { fileName: 'auth.middleware.js', path: '/backend/middleware', timestamp: '12s ago' },
    { fileName: 'CartContext.jsx', path: '/src/context', timestamp: '14s ago' },
    { fileName: 'users.migration.sql', path: '/database/migrations', timestamp: '16s ago' },
    { fileName: 'README.md', path: '/root', timestamp: '18s ago' }
  ];

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => {
            navigate('/file-explorer', {
              state: {
                projectName: 'E-Commerce Platform',
                prompt: promptData?.prompt
              }
            });
          }, 1000);
          return 100;
        }
        return prev + 2;
      });
    }, 400);

    return () => clearInterval(progressInterval);
  }, [navigate, promptData?.prompt]);

  useEffect(() => {
    const newStep = Math.min(Math.floor(progress / 20) + 1, 5);
    setCurrentStep(newStep);
    
    const remaining = Math.max(Math.floor((100 - progress) * 0.45), 0);
    setEstimatedTime(remaining);
  }, [progress]);

  useEffect(() => {
    const activityInterval = setInterval(() => {
      if (activities?.length < mockActivities?.length) {
        setActivities((prev) => [
          mockActivities?.[prev?.length],
          ...prev?.slice(0, 9)
        ]);
      }
    }, 2000);

    return () => clearInterval(activityInterval);
  }, [activities?.length]);

  const handleCancelGeneration = () => {
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    navigate('/prompt-input');
  };

  const getStepStatus = (stepId) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'active';
    return 'pending';
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation />
      <div className="content-wrapper">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8 lg:mb-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  Generating Your Project
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  AI is creating your complete software project with all necessary files
                </p>
              </div>
              <Button
                variant="outline"
                iconName="X"
                iconPosition="left"
                onClick={handleCancelGeneration}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
            </div>
          </div>

          <div className="mb-8 md:mb-10 lg:mb-12">
            <GenerationProgressIndicator
              progress={progress}
              currentStep={currentStep}
              estimatedTime={estimatedTime}
              steps={generationSteps}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-10">
            <div className="space-y-4 md:space-y-5 lg:space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="ListChecks" size={20} className="text-primary" />
                <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
                  Generation Steps
                </h2>
              </div>
              {generationSteps?.map((step) => (
                <GenerationStepCard
                  key={step?.id}
                  step={step?.label}
                  status={getStepStatus(step?.id)}
                  description={step?.description}
                />
              ))}
            </div>

            <div className="space-y-6 md:space-y-8">
              <LiveActivityFeed activities={activities} />
              <PromptSummaryCard
                prompt={promptData?.prompt}
                projectType={promptData?.projectType}
                techStack={promptData?.techStack}
              />
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border p-4 md:p-5 lg:p-6">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-sm md:text-base font-semibold text-foreground mb-2">
                  What's Being Generated
                </h3>
                <ul className="space-y-2 text-xs md:text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Icon name="CheckCircle2" size={14} className="text-success flex-shrink-0" />
                    <span>Complete React frontend with components and routing</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="CheckCircle2" size={14} className="text-success flex-shrink-0" />
                    <span>Node.js backend with API routes and authentication</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="CheckCircle2" size={14} className="text-success flex-shrink-0" />
                    <span>PostgreSQL database schemas and migrations</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="CheckCircle2" size={14} className="text-success flex-shrink-0" />
                    <span>README documentation and setup instructions</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="CheckCircle2" size={14} className="text-success flex-shrink-0" />
                    <span>Environment configuration and deployment guides</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CancelGenerationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
};

export default CodeGeneration;